"use client";

// The model selector: a ghost button in the composer footer opening the
// flag-filtered model list as a dropdown, with an "Auto" row on top that
// defers to the server (its default model, or per-message routing when the
// host says Auto is routed). Fully controlled — the catalog, the
// selection, and persistence all live in the host via the `models` source
// prop (`RichComposerProps.models`); absent → no model button. Model ids
// are plain `string` inside the block; hosts that own a narrower id type
// (the private `ModelId` union) narrow at their own boundary. The optional
// reasoning-level ("thinking depth") control is likewise fully host-driven:
// the host decides whether the effective model supports one and what the
// default is, so the block never imports the private model catalog.
import { c as _c } from "react/compiler-runtime";
import type { SdkModel } from "@bg-bgi/magnus-sdk";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { Check, ChevronDown, InfoIcon } from "lucide-react";
import { useState } from "react";

/** Reserved `selectedId` meaning "let the server pick" (the Auto row). */
export const AUTO_MODEL_SELECTION = "auto";

/** The retired Smart Auto selection; a stale one reads as Auto, which is what
 *  the server runs it as. */
const LEGACY_SMART_AUTO_SELECTION = "smart-auto";

/**
 * The three thinking-depth options rendered by the reasoning control. Kept as
 * plain block-local data (stable UI labels over the `"low" | "medium" | "high"`
 * wire values) so the block stays free of the private model catalog.
 */
const REASONING_LEVELS: Array<{
  label: string;
  value: string;
}> = [{
  label: "Low",
  value: "low"
}, {
  label: "Standard",
  value: "medium"
}, {
  label: "High",
  value: "high"
}];
const FALLBACK_REASONING_LEVEL = "medium";

/**
 * The composer model-picker seam. Absent on `RichComposerProps` → no model
 * button (the degraded default). Everything is host-supplied so the block
 * stays catalog-agnostic.
 */
export interface ComposerModelsSource {
  /**
   * Whether Auto routes each message server-side for this caller (routing on,
   * and not a surface that never routes). Only changes the Auto row's copy;
   * absent/false → Auto reads as the default model.
   */
  autoRouted?: boolean;
  /** The server's default model id — labels the Auto row. */
  defaultId?: string;
  /** The server's default reasoning level — the unlabeled "common case". */
  defaultReasoningLevel?: string;
  /** The caller's selectable models (already flag-filtered server-side). */
  items: SdkModel[];
  loading?: boolean;
  /** Fired when the user changes the reasoning level. */
  onReasoningLevelChange?: (level: string) => void;
  /** Fired with the picked id, or `AUTO_MODEL_SELECTION` for Auto. */
  onSelect: (id: string) => void;
  /**
   * Current thinking depth for the effective model. Omit
   * `onReasoningLevelChange` to hide the control entirely.
   */
  reasoningLevel?: string;
  /** The current selection; `AUTO_MODEL_SELECTION` (or absent) = Auto. */
  selectedId?: string;
  /**
   * Whether the *effective* model (Auto → the default) supports a reasoning
   * level. Host-computed because only the host knows the model catalog; the
   * block just gates the control on it.
   */
  supportsReasoningLevel?: boolean;
}

/** Model picker dropdown (hidden unless the host supplies `models`). */
export function ModelSelector(t0) {
  const $ = _c(56);
  const {
    models
  } = t0;
  const [showInfo, setShowInfo] = useState(false);
  const autoRouted = Boolean(models.autoRouted);
  const requestedId = models.selectedId ?? AUTO_MODEL_SELECTION;
  const selectedId = requestedId === LEGACY_SMART_AUTO_SELECTION ? AUTO_MODEL_SELECTION : requestedId;
  const isAuto = selectedId === AUTO_MODEL_SELECTION;
  let t1;
  if ($[0] !== isAuto || $[1] !== models.items || $[2] !== selectedId) {
    t1 = isAuto ? null : models.items.find(m => m.id === selectedId);
    $[0] = isAuto;
    $[1] = models.items;
    $[2] = selectedId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const selectedModel = t1;
  const displayName = isAuto ? "Auto" : selectedModel?.name ?? "Auto";
  let t2;
  if ($[4] !== models.defaultId || $[5] !== models.items) {
    t2 = models.items.find(m_0 => m_0.id === models.defaultId)?.name;
    $[4] = models.defaultId;
    $[5] = models.items;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const autoModelName = t2;
  const defaultLevel = models.defaultReasoningLevel ?? FALLBACK_REASONING_LEVEL;
  const level = models.reasoningLevel ?? defaultLevel;
  const showReasoning = Boolean(models.onReasoningLevelChange) && Boolean(models.supportsReasoningLevel);
  let t3;
  if ($[7] !== defaultLevel || $[8] !== level || $[9] !== showReasoning) {
    t3 = showReasoning && level !== defaultLevel ? REASONING_LEVELS.find(option => option.value === level)?.label : undefined;
    $[7] = defaultLevel;
    $[8] = level;
    $[9] = showReasoning;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const levelSuffix = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setShowInfo(false);
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Button className="min-w-0 shrink gap-2 pr-2 rounded-lg text-foreground" size="sm" type="button" variant="ghost" />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== levelSuffix) {
    t6 = levelSuffix && <span className="text-muted-foreground"> · {levelSuffix}</span>;
    $[13] = levelSuffix;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== displayName || $[16] !== t6) {
    t7 = <span className="truncate text-sm">{displayName}{t6}</span>;
    $[15] = displayName;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronDown className="size-4 shrink-0" />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7) {
    t9 = <DropdownMenuTrigger render={t5}>{t7}{t8}</DropdownMenuTrigger>;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-xs text-muted-foreground">Choose how Magnus responds</span>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e => {
      e.stopPropagation();
      setShowInfo(_temp);
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center justify-between px-2 py-1.5">{t10}<button className="rounded-full p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={t11} type="button"><InfoIcon className="size-3" /></button></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== autoRouted || $[25] !== showInfo) {
    t13 = showInfo && <div className="mx-2 mb-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground leading-relaxed space-y-1.5"><p>Think of models like different brains — each one thinks in its own way. Some are faster, some go deeper, and some are better at certain tasks like writing or analysis.</p>{autoRouted ? <p>You don't need to pick one — <strong>Auto</strong> chooses the right brain for each message. But if you're curious, try switching and see how the responses feel different.</p> : <p>You don't need to pick one — <strong>Auto</strong> uses our default. But if you're curious, try switching and see how the responses feel different.</p>}</div>;
    $[24] = autoRouted;
    $[25] = showInfo;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== models) {
    t14 = () => models.onSelect(AUTO_MODEL_SELECTION);
    $[27] = models;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="font-medium">Auto</span>;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = autoRouted ? "Picks the best model for each message" : `Our default${autoModelName ? ` — ${autoModelName}` : ""}`;
  let t17;
  if ($[30] !== t16) {
    t17 = <div className="flex flex-1 flex-col gap-0.5">{t15}<span className="text-xs text-muted-foreground">{t16}</span></div>;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== isAuto) {
    t18 = isAuto && <Check className="size-4 shrink-0" />;
    $[32] = isAuto;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t14 || $[35] !== t17 || $[36] !== t18) {
    t19 = <DropdownMenuItem className="gap-2" onClick={t14}>{t17}{t18}</DropdownMenuItem>;
    $[34] = t14;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <DropdownMenuSeparator />;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== models || $[40] !== selectedId) {
    t21 = models.items.map(model => <DropdownMenuItem className="gap-2" key={model.id} onClick={() => models.onSelect(model.id)}><div className="flex flex-1 flex-col gap-0.5"><div className="flex items-center gap-1.5"><span className="font-medium">{model.name}</span>{model.tag && <Badge className="text-[10px] px-1.5 py-0 h-4 font-normal" variant="secondary">{model.tag}</Badge>}</div>{model.description && <span className="text-xs text-muted-foreground">{model.description}</span>}</div>{selectedId === model.id && <Check className="size-4 shrink-0" />}</DropdownMenuItem>);
    $[39] = models;
    $[40] = selectedId;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t13 || $[43] !== t19 || $[44] !== t21) {
    t22 = <div className="-mx-1 min-h-0 overflow-y-auto overflow-x-hidden px-1">{t12}{t13}{t19}{t20}{t21}</div>;
    $[42] = t13;
    $[43] = t19;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== level || $[47] !== models || $[48] !== showReasoning) {
    t23 = showReasoning && models.onReasoningLevelChange && <div className="shrink-0"><DropdownMenuSeparator /><div className="flex items-center justify-between gap-2 px-2 py-1.5"><span className="text-xs text-muted-foreground">Thinking</span><div className="flex gap-0.5 rounded-lg bg-muted p-0.5">{REASONING_LEVELS.map(option_0 => <button aria-label={`Thinking: ${option_0.label}`} aria-pressed={level === option_0.value} className={cn("rounded-md px-2 py-0.5 text-xs transition-colors", level === option_0.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")} key={option_0.value} onClick={e_0 => {
            e_0.stopPropagation();
            models.onReasoningLevelChange?.(option_0.value);
          }} type="button">{option_0.label}</button>)}</div></div></div>;
    $[46] = level;
    $[47] = models;
    $[48] = showReasoning;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <DropdownMenuContent align="start" className="flex w-72 max-h-[min(400px,var(--available-height))] flex-col overflow-y-hidden" side="top" sideOffset={8}>{t22}{t23}</DropdownMenuContent>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t24 || $[54] !== t9) {
    t25 = <DropdownMenu onOpenChange={t4}>{t9}{t24}</DropdownMenu>;
    $[53] = t24;
    $[54] = t9;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  return t25;
}
function _temp(prev) {
  return !prev;
}